import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Mail } from 'lucide-react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Button } from '@/components/ui/button';
import { EmailKindBadge } from '@/components/billing/invoices/EmailKindBadge';
import { EmailStatusBadge } from '@/components/billing/invoices/EmailStatusBadge';
import type { billing_TimelineEntryResponse } from '@/api/models/billing_TimelineEntryResponse';
import { formatDateTime } from '@/lib/format';

interface Props {
  timeline: billing_TimelineEntryResponse[];
  pageSize?: number;
}


// Map a dunning sweep stage (`reminder_1`, ..., `final_repeat`) to the
// email-log kind so the timeline can reuse EmailKindBadge.
const STAGE_TO_EMAIL_KIND: Record<string, string> = {
  reminder_1: 'dunning_reminder_1',
  reminder_2: 'dunning_reminder_2',
  reminder_3: 'dunning_reminder_3',
  final_notice: 'dunning_final_notice',
  final_repeat: 'dunning_final_repeat',
};

export function DunningWorkflowTimeline({ timeline, pageSize = 50 }: Props) {
  const { i18n } = useLingui();
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const sorted = useMemo(() => {
    const copy = [...timeline];
    copy.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return tb - ta;
    });
    return copy;
  }, [timeline]);

  const visible = sorted.slice(0, visibleCount);
  const hasMore = sorted.length > visibleCount;

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="dunning-timeline-empty">
        <Trans>No transitions yet.</Trans>
      </p>
    );
  }

  return (
    <div data-testid="dunning-workflow-timeline">
      <ol className="space-y-2 text-sm">
        {visible.map((entry) => (
          <li key={entry.id ?? `${entry.at}-${entry.action}`} className="border-l-2 pl-3 py-1">
            <div className="text-muted-foreground text-xs">
              {entry.at ? formatDateTime(entry.at, i18n.locale) : '—'}
              {entry.actor_name && (
                <span className="ml-2">— {entry.actor_name}</span>
              )}
            </div>
            <div>
              <span className="font-medium">{entry.action || entry.type}</span>
              {entry.from_state && entry.to_state && (
                <span className="ml-1 text-muted-foreground">
                  ({entry.from_state} → {entry.to_state})
                </span>
              )}
              {entry.stage && STAGE_TO_EMAIL_KIND[entry.stage] && (
                <span className="ml-2">
                  <EmailKindBadge kind={STAGE_TO_EMAIL_KIND[entry.stage]} />
                </span>
              )}
              {entry.email_status && (
                <span className="ml-2 inline-flex" data-testid="email-status-pill">
                  <EmailStatusBadge status={entry.email_status} />
                </span>
              )}
              {entry.email_log_id && (
                <Link
                  to={`/billing/email-cockpit?email_log_id=${encodeURIComponent(entry.email_log_id)}`}
                  onClick={(e) => e.stopPropagation()}
                  className="ml-2 inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
                  data-testid="timeline-email-link"
                >
                  <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <Trans>View email</Trans>
                </Link>
              )}
            </div>
            {entry.comment && (
              <div className="text-muted-foreground italic text-xs mt-1">{entry.comment}</div>
            )}
          </li>
        ))}
      </ol>
      {hasMore && (
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setVisibleCount((c) => c + pageSize)}
            data-testid="timeline-load-older"
          >
            <Trans>Load older</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}
